import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import api from '../api';
import PageHeader from './PageHeader';

export default function MediaUpload({ onSuccess, onBack }) {
  const [guestName, setGuestName] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const validTypes = ['image/jpeg', 'image/png', 'video/mp4'];
    const maxSize = 2048 * 1024 * 1024; // 2GB

    const valid = selected.filter(f => {
      if (!validTypes.includes(f.type)) {
        alert(`${f.name}: Geçersiz format. Sadece JPG, PNG ve MP4.`);
        return false;
      }
      if (f.size > maxSize) {
        alert(`${f.name}: Dosya boyutu 2GB'dan büyük.`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...valid]);

    // Önizleme oluştur
    valid.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPreviews(prev => [...prev, { name: file.name, url: ev.target.result, type: 'image' }]);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviews(prev => [...prev, { name: file.name, url: null, type: 'video' }]);
      }
    });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!guestName.trim() || files.length === 0) return;
    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('guestName', guestName.trim());
      formData.append('screenSize', window.screen.width + 'x' + window.screen.height);
      formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
      formData.append('platform', navigator.platform);
      files.forEach(f => formData.append('media', f));

      await api.post('/api/uploads', formData, {
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setProgress(pct);
        }
      });

      onSuccess(`${files.length} dosya başarıyla yüklendi! 📸`);
      setFiles([]);
      setPreviews([]);
      setGuestName('');
      setProgress(0);
    } catch {
      alert('Yükleme sırasında bir hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-6 pt-14 pb-8 max-w-md mx-auto">
      <PageHeader title="Fotoğraf & Video" onBack={onBack} />

      {/* Başlık */}
      <div className="text-center mb-8 mt-4">
        <h2 className="font-playfair text-2xl text-sage-800 font-semibold">Fotoğraf & Video</h2>
        <p className="text-sage-500 text-sm mt-2">Anlarınızı bizimle paylaşın</p>
      </div>

      <div className="w-full space-y-5">
        {/* İsim */}
        <div>
          <label className="block text-sage-600 text-sm font-medium mb-2">Adınız</label>
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Adınız ve soyadınız"
            maxLength={100}
            className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-sage-200 rounded-xl
                       text-sage-800 placeholder:text-sage-300 focus:outline-none focus:border-gold-400
                       focus:ring-2 focus:ring-gold-300/20 transition-all"
          />
        </div>

        {/* Dosya seçme alanı */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-full py-10 border-2 border-dashed border-sage-200 rounded-2xl
                     bg-white/50 hover:bg-white/70 cursor-pointer transition-all
                     flex flex-col items-center justify-center gap-3"
        >
          <div className="w-14 h-14 bg-sage-100 rounded-full flex items-center justify-center">
            <svg className="w-7 h-7 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-sage-600 text-sm font-medium">Dosya seçmek için dokunun</p>
            <p className="text-sage-400 text-xs mt-1">JPG, PNG, MP4 — Maks. 2GB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,video/mp4"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Seçilen dosyaların önizlemesi */}
        {previews.length > 0 && (
          <div className="space-y-2">
            <p className="text-sage-600 text-sm font-medium">{previews.length} dosya seçildi</p>
            <div className="grid grid-cols-3 gap-2">
              {previews.map((preview, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative aspect-square rounded-xl overflow-hidden bg-sage-100"
                >
                  {preview.type === 'image' && preview.url ? (
                    <img src={preview.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-sage-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white text-xs"
                  >
                    ✕
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {uploading && (
          <div className="w-full">
            <div className="flex justify-between text-xs text-sage-500 mb-1">
              <span>Yükleniyor...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full h-2 bg-sage-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="h-full bg-gradient-to-r from-sage-500 to-gold-500 rounded-full progress-pulse"
              />
            </div>
          </div>
        )}

        {/* Yükle Butonu */}
        <button
          onClick={handleUpload}
          disabled={!guestName.trim() || files.length === 0 || uploading}
          className="w-full py-3.5 bg-sage-600 text-white rounded-xl font-medium
                     hover:bg-sage-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed
                     active:scale-[0.98]"
        >
          {uploading ? `Yükleniyor... %${progress}` : `${files.length > 0 ? files.length + ' Dosyayı ' : ''}Yükle`}
        </button>
      </div>
    </div>
  );
}
